import Modal from "Common/Components/Modal";
import useTransactions from "hooks/useTransactions";
import { ArrowDownCircle, ArrowUpCircle, Wallet } from "lucide-react";
import { useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { ToastContainer, toast } from "react-toastify";
import { approveWithdrawal } from "services/general.service";
import { ITransaction } from "types/dashboard.service";
import { formatCurrency, nairaSymbol, truncateWords } from "utills/helper";
import OtpForm from "./OtpForm";
import BreadCrumb from "Common/BreadCrumb";
import { useFocusedRow } from "hooks/useFocusedRecord";
import {
  FocusNotFound,
  focusRowClass,
  scrollRowIntoView,
} from "Common/FocusedRow";

const STATUS_COLORS: Record<string, string> = {
  successful: "text-green-600 bg-green-50",
  Pending: "text-yellow-600 bg-yellow-50",
  Initiated: "text-gray-500 bg-gray-50",
  Approval: "text-blue-600 bg-blue-50",
  failed: "text-red-600 bg-red-50",
};

export default function Transactions() {
  const { data, isLoading, hasNextPage, handleEndReached } = useTransactions(10);
  const [showForm, setShowForm] = useState(false);
  const [transaction, setTransaction] = useState<ITransaction | null>(null);
  const queryClient = useQueryClient();

  // Withdrawal and payment alerts both land here. There is no per-transaction
  // route, so the alert's id arrives as `?focus=` and rings the row.
  const { notFound: focusNotFound, dismissFocus, isFocused } = useFocusedRow(
    data,
    isLoading
  );

  const { isLoading: requesting, mutate } = useMutation(
    async (input: { otp: string; id: string }) => await approveWithdrawal(input.id, input.otp),
    {
      onSuccess(data) { toast.success(data?.message); queryClient.invalidateQueries(); toggleConfirm(); },
      onError(error: any) { toast.error(error?.response?.data?.message); },
    }
  );

  const toggleConfirm = () => setShowForm((v) => !v);

  const handleWithdraw = (e: React.MouseEvent, tx: ITransaction) => {
    e.stopPropagation();
    setTransaction(tx);
    toggleConfirm();
  };

  const handleSubmit = (otp: string) => {
    mutate({ otp, id: transaction?._id! });
  };

  return (
    <div>
      <BreadCrumb title="Transactions" pageTitle="Transactions" />
      <ToastContainer closeButton={false} limit={1} />

      {focusNotFound && <FocusNotFound onDismiss={dismissFocus} />}

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="py-12 text-center text-sm text-gray-400">Loading transactions…</div>
        ) : (data?.length ?? 0) === 0 ? (
          <div className="py-16 text-center">
            <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Wallet size={22} className="text-gray-400" />
            </div>
            <p className="text-sm text-gray-500">No transactions yet</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table data-stack className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">User</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Status / Type</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Narration</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Date</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data?.map((tx) => (
                    <tr
                      key={tx._id}
                      ref={scrollRowIntoView(isFocused(tx._id))}
                      className={`hover:bg-gray-50 transition-colors ${focusRowClass(
                        isFocused(tx._id)
                      )}`}
                    >
                      <td data-label="User" data-cell="title" className="px-4 py-3 font-medium text-gray-900">
                        {tx?.user?.firstName} {tx?.user?.lastName}
                      </td>
                      <td data-label="Amount" className="px-4 py-3 text-gray-700 font-medium">
                        {nairaSymbol}{formatCurrency(tx?.amount)}
                      </td>
                      <td data-label="Status / Type" className="px-4 py-3">
                        <div className="flex flex-col gap-1">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[tx?.status] ?? "text-gray-600 bg-gray-50"}`}>
                            {tx?.status}
                          </span>
                          <span className={`flex items-center gap-1 text-xs ${tx?.type === "Debit" ? "text-red-500" : "text-green-600"}`}>
                            {tx?.type === "Debit"
                              ? <ArrowUpCircle size={12} />
                              : <ArrowDownCircle size={12} />}
                            {tx?.type}
                          </span>
                        </div>
                      </td>
                      <td data-label="Narration" data-cell="block" className="px-4 py-3 text-gray-500">{truncateWords(tx.narration || "", 40)}</td>
                      <td data-label="Date" className="px-4 py-3 text-gray-500">
                        {new Date(tx.createdAt).toLocaleDateString()}{" "}
                        {new Date(tx.createdAt).toLocaleTimeString()}
                      </td>
                      <td data-label="Action" data-cell="actions" className="px-4 py-3 text-right">
                        {tx.status === "Approval" && (
                          <button
                            onClick={(e) => handleWithdraw(e, tx)}
                            className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium text-white bg-green-600 hover:bg-green-700 transition-colors"
                          >
                            Approve
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {hasNextPage && (
              <div className="flex justify-end px-4 py-3 border-t border-gray-100">
                <button
                  className="text-sm text-indigo-600 hover:underline"
                  onClick={() => handleEndReached()}
                >
                  Load more
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <Modal
        show={showForm}
        onHide={toggleConfirm}
        id="withdrawalModal"
        modal-center="true"
        size="md"
      >
        <Modal.Header
          className="flex items-center justify-between px-5 py-4 border-b border-gray-200"
          closeButtonClass="transition-all duration-200 ease-linear text-slate-500 hover:text-red-500"
        >
          <Modal.Title className="text-base font-semibold text-gray-900">Confirm Withdrawal</Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-5 py-4 overflow-y-auto">
          <p className="text-sm text-gray-600 mb-4">
            Approve {nairaSymbol}{formatCurrency(transaction?.amount!)} withdrawal for{" "}
            <span className="font-medium">{transaction?.user?.firstName} {transaction?.user?.lastName}</span>
          </p>
          <OtpForm onCancel={toggleConfirm} onSubmit={handleSubmit} isLoading={requesting} message="" />
        </Modal.Body>
      </Modal>
    </div>
  );
}
